import { useParams } from 'react-router-dom'
import { useRequestsEvents } from '../../hooks/useRequestsEvents'
import { useEnvironment } from '../../hooks/useEnvironment'
import React, { useEffect, useRef, useState } from 'react'
import {
  EmptyResult,
  ErrorResult,
  ForbiddenResult,
  LoaderBackDrop
} from '../feedbacks'
import { PromotionTable } from './tables/PromotionTable'
import _ from 'lodash'
import { Button, Panel, Progress } from 'rsuite'
import { PanelHeader } from '../panels'
import {
  RiDeleteBinLine,
  RiImageAddLine,
  RiImageLine,
  RiProfileLine
} from 'react-icons/ri'

export function PromotionPanel (props) {
  // get current promotion id
  const { promotionId } = useParams()

  // requests
  const {
    getPromotion,
    uploadPromotionImage,
    deletePromotionImage
  } = useRequestsEvents()

  // permissions
  const { hasPromotionPermissions } = useEnvironment()

  const hasReadPromotionPermission = hasPromotionPermissions(
    promotionId,
    'read:promotion'
  )

  const hasUpdatePromotionPermission = hasPromotionPermissions(
    promotionId,
    'update:promotion'
  )

  // hidden file input
  const fileInputRef = useRef(null)

  // selected image
  const [selectedFile, setSelectedFile] = useState(null)

  // local image preview
  const [selectedFilePreview, setSelectedFilePreview] = useState(null)

  // image hover
  const [imageHovered, setImageHovered] = useState(false)

  // upload progress
  const [uploadProgress, setUploadProgress] = useState(0)

  // states
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // force image reload after S3 overwrite
  const [imageVersion, setImageVersion] = useState(Date.now())

  // component config
  const [componentCfg, setComponentCfg] = useState({
    data: {},
    isLoaded: false,
    isError: false
  })

  // load promotion
  useEffect(() => {
    let isMounted = true

    if (componentCfg.isLoaded) return

    getPromotion(
      promotionId,
      (result) => {
        if (!isMounted) return null

        setComponentCfg({
          ...componentCfg,
          isError: false,
          isLoaded: true,
          data: result.data
        })
      },
      () => {
        if (!isMounted) return null

        setComponentCfg({
          ...componentCfg,
          isError: true,
          isLoaded: true,
          data: {}
        })
      }
    )

    return () => {
      isMounted = false
    }
  }, [componentCfg.isLoaded])

  // cleanup preview
  useEffect(() => {
    return () => {
      if (selectedFilePreview) {
        URL.revokeObjectURL(selectedFilePreview)
      }
    }
  }, [selectedFilePreview])

  // select image
  const handleSelectImage = (event) => {
    const file = event.target.files &&
      event.target.files[0]

    if (!file) return

    const acceptedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp'
    ]

    if (!acceptedTypes.includes(file.type)) {
      window.alert(
        'La imagen debe ser JPG, PNG o WEBP.'
      )

      event.target.value = ''
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      window.alert(
        'La imagen no puede superar los 5 MB.'
      )

      event.target.value = ''
      return
    }

    if (selectedFilePreview) {
      URL.revokeObjectURL(selectedFilePreview)
    }

    setSelectedFile(file)
    setSelectedFilePreview(
      URL.createObjectURL(file)
    )
  }

  // open native selector
  const handleOpenFileSelector = () => {
    if (
      !hasUpdatePromotionPermission ||
      isUploading ||
      isDeleting
    ) {
      return
    }

    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // cancel selected image
  const handleCancelSelectedImage = () => {
    if (selectedFilePreview) {
      URL.revokeObjectURL(selectedFilePreview)
    }

    setSelectedFile(null)
    setSelectedFilePreview(null)
    setUploadProgress(0)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // upload image
  const handleUploadImage = () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadProgress(0)

    uploadPromotionImage(
      promotionId,
      selectedFile,
      (result) => {
        const imageUrl = _.get(
          result,
          'data.promotion_image_url',
          null
        )

        setComponentCfg(prev => ({
          ...prev,
          data: {
            ...prev.data,
            promotion_image_url: imageUrl
          }
        }))

        setImageVersion(Date.now())

        if (selectedFilePreview) {
          URL.revokeObjectURL(
            selectedFilePreview
          )
        }

        setSelectedFile(null)
        setSelectedFilePreview(null)

        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }

        setUploadProgress(100)
        setIsUploading(false)
      },
      () => {
        setIsUploading(false)
        setUploadProgress(0)
      },
      (progress) => {
        setUploadProgress(
          Math.floor(
            _.get(
              progress,
              'percent',
              0
            )
          )
        )
      }
    )
  }

  // delete image
  const handleDeleteImage = () => {
    const confirmDelete = window.confirm(
      '¿Seguro que quieres eliminar la imagen de esta promoción?'
    )

    if (!confirmDelete) return

    setIsDeleting(true)

    deletePromotionImage(
      promotionId,
      () => {
        setComponentCfg(prev => ({
          ...prev,
          data: {
            ...prev.data,
            promotion_image_url: null
          }
        }))

        handleCancelSelectedImage()

        setIsDeleting(false)
      },
      () => {
        setIsDeleting(false)
      }
    )
  }

  // current image
  const promotionImageUrl = _.get(
    componentCfg,
    'data.promotion_image_url',
    null
  )

  // preview or stored image
  const displayedImage =
    selectedFilePreview ||
    (
      promotionImageUrl
        ? `${promotionImageUrl}?v=${imageVersion}`
        : null
    )

  return (
    <>
      <Panel
        bodyFill
        header={
          <PanelHeader
            icon={<RiProfileLine />}
            title={'dashboard.description'}
            hasDelete={false}
            hasEdit={false}
            hasCreate={false}
            hasReload={false}
          />
        }
      >
        <LoaderBackDrop
          active={!componentCfg.isLoaded}
          loader
          background={'white'}
          top
        >
          <Panel
            className={'filled-table-panel'}
            style={{
              borderRadius: '4px'
            }}
            bodyFill
          >
            {(() => {
              if (!hasReadPromotionPermission) {
                return (
                  <ForbiddenResult
                    compact={true}
                  />
                )
              }

              if (componentCfg.isError) {
                return (
                  <ErrorResult
                    compact={true}
                  />
                )
              }

              if (_.isEmpty(componentCfg.data)) {
                return (
                  <EmptyResult
                    compact={true}
                  />
                )
              }

              return (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: '15px',
                      width: '100%'
                    }}
                  >
                    {/* PROMOTION DATA */}
                    <div
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0
                      }}
                    >
                      <PromotionTable
                        data={componentCfg.data}
                      />
                    </div>

                    {/* PROMOTION IMAGE */}
                    <div
                      style={{
                        width: '280px',
                        minWidth: '280px'
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type={'file'}
                        accept={
                          'image/jpeg,image/png,image/webp'
                        }
                        onChange={handleSelectImage}
                        style={{
                          display: 'none'
                        }}
                      />

                      <div
                        style={{
                          marginBottom: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          color: '#555'
                        }}
                      >
                        <RiImageLine
                          size={15}
                        />

                        <span>
                          Imagen
                        </span>
                      </div>

                      <div
                        onMouseEnter={() => {
                          setImageHovered(true)
                        }}
                        onMouseLeave={() => {
                          setImageHovered(false)
                        }}
                        onClick={
                          !displayedImage
                            ? handleOpenFileSelector
                            : undefined
                        }
                        style={{
                          position: 'relative',
                          height: '170px',
                          overflow: 'hidden',
                          borderRadius: '4px',
                          border:
                            '1px solid #ddd',
                          background: '#fafafa',
                          cursor:
                            !displayedImage &&
                            hasUpdatePromotionPermission
                              ? 'pointer'
                              : 'default'
                        }}
                      >
                        {displayedImage
                          ? (
                            <img
                              src={displayedImage}
                              alt={'Promoción'}
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'block',
                                objectFit: 'cover'
                              }}
                            />
                            )
                          : (
                            <div
                              style={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#aaa',
                                gap: '6px'
                              }}
                            >
                              <RiImageAddLine
                                size={28}
                              />

                              <span
                                style={{
                                  fontSize: '12px'
                                }}
                              >
                                Añadir imagen
                              </span>
                            </div>
                            )}

                        {displayedImage &&
                          hasUpdatePromotionPermission &&
                          imageHovered &&
                          !selectedFile && (
                            <div
                              onClick={
                                handleOpenFileSelector
                              }
                              style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background:
                                  'rgba(0, 0, 0, 0.38)',
                                color: '#fff',
                                cursor: 'pointer'
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '13px',
                                  fontWeight: 500
                                }}
                              >
                                <RiImageAddLine
                                  size={18}
                                />

                                Cambiar imagen
                              </div>
                            </div>
                        )}

                        {selectedFile && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '8px',
                              bottom: '8px',
                              padding:
                                '3px 7px',
                              background:
                                'rgba(0, 0, 0, 0.6)',
                              color: '#fff',
                              borderRadius: '3px',
                              fontSize: '10px'
                            }}
                          >
                            Vista previa
                          </div>
                        )}
                      </div>

                      {/* NEW IMAGE ACTIONS */}
                      {selectedFile && (
                        <div
                          style={{
                            marginTop: '8px',
                            display: 'flex',
                            gap: '6px'
                          }}
                        >
                          <Button
                            appearance={'primary'}
                            size={'sm'}
                            style={{
                              flex: 1
                            }}
                            loading={isUploading}
                            disabled={
                              isUploading ||
                              isDeleting
                            }
                            onClick={
                              handleUploadImage
                            }
                          >
                            Guardar
                          </Button>

                          <Button
                            appearance={'default'}
                            size={'sm'}
                            disabled={
                              isUploading ||
                              isDeleting
                            }
                            onClick={
                              handleCancelSelectedImage
                            }
                          >
                            Cancelar
                          </Button>
                        </div>
                      )}

                      {/* CURRENT IMAGE ACTIONS */}
                      {!selectedFile &&
                        promotionImageUrl &&
                        hasUpdatePromotionPermission && (
                          <div
                            style={{
                              marginTop: '6px',
                              display: 'flex',
                              justifyContent:
                                'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <button
                              type={'button'}
                              onClick={
                                handleOpenFileSelector
                              }
                              style={{
                                padding: 0,
                                border: 0,
                                background:
                                  'transparent',
                                color: '#3498ff',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              Cambiar imagen
                            </button>

                            <button
                              type={'button'}
                              disabled={isDeleting}
                              onClick={
                                handleDeleteImage
                              }
                              style={{
                                padding: 0,
                                border: 0,
                                background:
                                  'transparent',
                                color: '#d9534f',
                                cursor:
                                  isDeleting
                                    ? 'default'
                                    : 'pointer',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems:
                                  'center',
                                gap: '3px'
                              }}
                            >
                              <RiDeleteBinLine
                                size={13}
                              />

                              Eliminar
                            </button>
                          </div>
                      )}

                      {isUploading && (
                        <div
                          style={{
                            marginTop: '8px'
                          }}
                        >
                          <Progress.Line
                            percent={
                              uploadProgress
                            }
                            status={
                              uploadProgress ===
                              100
                                ? 'success'
                                : 'active'
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )
            })()}
          </Panel>
        </LoaderBackDrop>
      </Panel>
    </>
  )
}